const PHONE_HELLO_WORDS = [
  'Hello',
  '¿Aló?',
  'Allô',
  'Hallo',
  'Pronto',
  'Alô',
  '喂',
  'もしもし',
  'Алло',
  'ألو',
];

const LOCALE_TO_INDEX = {
  en: 0,
  es: 1,
  fr: 2,
  de: 3,
  it: 4,
  pt: 5,
  zh: 6,
  ja: 7,
  ru: 8,
  ar: 9,
};

const LAUNCHER_POSITION_CLASSNAMES = [
  'bb-pos-bottom-right',
  'bb-pos-bottom-left',
  'bb-pos-bottom-center',
];

function getLauncherPositionClass(position) {
  switch (position) {
    case 'bottom-left':
      return 'bb-pos-bottom-left';
    case 'bottom-center':
      return 'bb-pos-bottom-center';
    case 'bottom-right':
    default:
      return 'bb-pos-bottom-right';
  }
}

function getStartIndexByLocale() {
  try {
    const lang = (navigator.language || navigator.userLanguage || '')
      .slice(0, 2)
      .toLowerCase();

    return LOCALE_TO_INDEX[lang] ?? 0;
  } catch (_) {
    return 0;
  }
}

function derivePulseColor(color) {
  if (!color || typeof color !== 'string') return null;

  const trimmed = color.trim();

  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) {
    const r = parseInt(trimmed.slice(1, 3), 16);
    const g = parseInt(trimmed.slice(3, 5), 16);
    const b = parseInt(trimmed.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, 0.7)`;
  }

  if (/^rgb\(/i.test(trimmed)) {
    return trimmed.replace('rgb', 'rgba').replace(')', ', 0.7)');
  }

  return null;
}

export function detachLauncherLabelCycler(buttonElement) {
  if (!buttonElement) return;

  const cycler = buttonElement.__bbLabelCycler;

  if (!cycler) return;

  try {
    buttonElement.removeEventListener('animationiteration', cycler.onIter);
  } catch (_) {}

  try {
    clearTimeout(cycler.kickTimeout);
  } catch (_) {}

  try {
    cycler.stopFallback && cycler.stopFallback();
  } catch (_) {}

  try {
    delete buttonElement.__bbLabelCycler;
  } catch (_) {}
}

export function attachLauncherLabelCycler({ buttonElement, mode }) {
  if (!buttonElement) return;

  detachLauncherLabelCycler(buttonElement);

  if (mode !== 'multilingual_hello') return;

  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let index = getStartIndexByLocale();
  buttonElement.dataset.label = PHONE_HELLO_WORDS[index];

  if (reduceMotion) return;

  const advance = () => {
    if (!buttonElement || buttonElement.dataset.state !== 'idle') return;
    index = (index + 1) % PHONE_HELLO_WORDS.length;
    buttonElement.dataset.label = PHONE_HELLO_WORDS[index];
  };

  const onIter = (event) => {
    if (!event || event.animationName !== 'bb-pulse') return;
    advance();

    if (
      buttonElement &&
      buttonElement.__bbLabelCycler &&
      buttonElement.__bbLabelCycler.stopFallback
    ) {
      buttonElement.__bbLabelCycler.stopFallback();
    }
  };

  buttonElement.addEventListener('animationiteration', onIter);

  let fallbackId = null;
  const startFallback = () => {
    if (!fallbackId) {
      fallbackId = setInterval(advance, 2000);
    }
  };
  const stopFallback = () => {
    if (fallbackId) {
      clearInterval(fallbackId);
      fallbackId = null;
    }
  };
  const kickTimeout = setTimeout(startFallback, 2500);

  buttonElement.__bbLabelCycler = {
    onIter,
    stopFallback,
    kickTimeout,
  };
}

export function applyLauncherAppearance({
  buttonElement,
  poweredByElement = null,
  appearance = {},
  fallbackLabel,
}) {
  if (!buttonElement) return;

  const wrapperElement = buttonElement.closest('#babelbeez-chat-wrapper');

  if (wrapperElement) {
    wrapperElement.classList.remove(...LAUNCHER_POSITION_CLASSNAMES);
    wrapperElement.classList.add(
      getLauncherPositionClass(appearance.button_position),
    );
  }

  if (appearance.button_bg_color) {
    buttonElement.style.setProperty(
      '--bb-button-bg-idle',
      appearance.button_bg_color,
    );
    buttonElement.style.setProperty(
      '--bb-button-bg-active',
      appearance.button_bg_color,
    );
    buttonElement.style.setProperty(
      '--bb-button-bg-loading',
      appearance.button_bg_color,
    );
    buttonElement.style.setProperty(
      '--bb-button-bg-rag-retrieval',
      appearance.button_bg_color,
    );
    buttonElement.style.setProperty(
      '--bb-button-bg-speaking',
      appearance.button_bg_color,
    );

    const pulseColor = derivePulseColor(appearance.button_bg_color);
    if (pulseColor) {
      buttonElement.style.setProperty('--bb-pulse-color', pulseColor);
    }

    if (poweredByElement) {
      poweredByElement.style.color = appearance.button_bg_color;
    }
  }

  if (appearance.button_text_color) {
    buttonElement.style.setProperty(
      '--bb-button-text-color',
      appearance.button_text_color,
    );
    buttonElement.style.setProperty(
      '--bb-button-icon-color',
      appearance.button_text_color,
    );
  }

  const mode = appearance.button_label_mode || 'custom';

  if (mode === 'multilingual_hello') {
    attachLauncherLabelCycler({ buttonElement, mode });
    return;
  }

  detachLauncherLabelCycler(buttonElement);

  const label =
    typeof appearance.button_label === 'string' && appearance.button_label.length > 0
      ? appearance.button_label
      : fallbackLabel;

  if (typeof label === 'string' && label.length > 0) {
    buttonElement.dataset.label = label;
  }
}